<script setup lang="ts">
/**
 * The visitor-facing consent step for `CONSENT_PURPOSES` — the notice
 * `privacy/consent.ts` was written for and nothing rendered.
 *
 * ── WHY IT IS INSIDE THE PANEL AND NOT A BANNER ─────────────────────────────
 *
 * A banner is the shape that trains people to click yes. It appears before
 * anyone has asked for anything, it blocks the thing they came for, and its
 * only escape is agreement — which is the definition of consent that is not
 * freely given (Art. 4(11)). This sits UNDER the transcript, beside the
 * composer, at the point where a conversation exists and the two facts it asks
 * about have become relevant: the agent is about to ask "which page are you
 * on?", and this is the offer to answer it in advance.
 *
 * The necessary set is deliberately NOT here and has no control. Its basis is
 * Art. 6(1)(b) — the visitor opened a panel and typed a question — and
 * `visitor-context.ts` argues at length why putting it behind a click would be
 * worse for the visitor. A notice that asked about all seven fields would also
 * make the two that are genuinely refusable look like the other five.
 *
 * ── THREE STATES PER PURPOSE, NOT TWO ──────────────────────────────────────
 *
 * `wasAsked` exists so this component can tell "they said no" from "we have not
 * asked", and the difference is visible: an unanswered purpose shows two
 * buttons, a granted one shows its value is shared plus a one-word withdrawal,
 * and a refused one shows nothing further. Collapsing refused into unasked would
 * put this notice back in front of a visitor who already declined, on every
 * page of the customer's site, until they gave in.
 *
 * ── WITHDRAWAL IS ONE CONTROL, IN THE SAME PLACE, WITH THE SAME WEIGHT ──────
 *
 * Art. 7(3) asks for withdrawal "as easy to withdraw as to give", and
 * `consent.ts` holds that property in its API — `revoke` takes exactly the
 * arguments `grant` does. This is the UI half of the same requirement: not a
 * link to a preferences screen, not a second dialog. One button, next to the
 * thing it takes back.
 *
 * ── NOTHING IS RENDERED WHEN THERE IS NOTHING TO DECIDE ─────────────────────
 *
 * `available` is what the caller has actually obtained from the host page, and
 * a purpose whose value has no transport is NOT offered. Today that means the
 * notice does not appear in production at all: `loader/protocol.ts`'s
 * `HostMessage` union carries `init`/`open`/`close`/`theme` and no page fields,
 * so `pagePath` and `referrer` are null at every call site.
 *
 * That is deliberate and it is the honest half. A notice shown to a real visitor
 * asking permission for data that cannot be collected is not a smaller wrong
 * than no notice — it is a question whose answer changes nothing, which is worse
 * than silence because it teaches that our questions do not matter. Wiring the
 * gate now means the day a `page` message is added to that union, the notice
 * appears with no further change and no chance of the transport landing first —
 * which is the ordering ADR-0129 chose for the whole module and for the same
 * reason.
 */
import { computed } from 'vue';

import type { WidgetMessageId } from '../i18n/messages';
import {
  CONSENT_PURPOSES,
  isGranted,
  wasAsked,
  type ConsentPurpose,
  type ConsentState,
} from '../privacy/consent';
import { useText } from '../vendor/i18n';
import { OwButton } from '../vendor/ui';

/**
 * Which message id names each purpose, as a closed map.
 *
 * `Record<ConsentPurpose, …>` and not a lookup with a fallback: adding a third
 * purpose to `CONSENT_PURPOSES` must fail to COMPILE here rather than render a
 * row whose label is a slug. A purpose the visitor cannot read the name of is a
 * purpose they cannot consent to.
 */
const PURPOSE_LABEL: Readonly<Record<ConsentPurpose, WidgetMessageId>> = {
  'page-context': 'widget.consent.page_label',
  'arrival-context': 'widget.consent.arrival_label',
};

const props = defineProps<{
  readonly consent: ConsentState;
  /**
   * The purposes whose data the host page has actually supplied. A purpose
   * outside this set is not offered — see the header.
   */
  readonly available: readonly ConsentPurpose[];
}>();

const emit = defineEmits<{
  grant: [purpose: ConsentPurpose];
  refuse: [purpose: ConsentPurpose];
}>();

const text = useText();

// Ordered by `CONSENT_PURPOSES`, never by `available`'s order or by decision
// order, so the list a visitor reads is stable between visits and does not
// reshuffle when they answer one of them.
const offered = computed(() =>
  CONSENT_PURPOSES.filter((purpose) => props.available.includes(purpose)),
);
const undecided = computed(() =>
  offered.value.filter((purpose) => !wasAsked(props.consent, purpose)),
);

/**
 * Every offered purpose already answered and none granted: the notice has done
 * its job and the panel goes back to being a chat panel. A granted purpose keeps
 * it on screen, because its withdrawal lives here.
 */
const visible = computed(
  () =>
    offered.value.length > 0 &&
    (undecided.value.length > 0 ||
      offered.value.some((purpose) => isGranted(props.consent, purpose))),
);

/**
 * A refused purpose renders nothing at all. It is not "greyed out" and it is
 * not a row saying "not shared" with a button beside it: either of those is a
 * standing invitation to reconsider, in a panel the visitor opened to ask about
 * something else. They said no; the offer is over for this tab.
 */
const rows = computed(() =>
  offered.value
    .map((purpose) => ({
      purpose,
      granted: isGranted(props.consent, purpose),
      asked: wasAsked(props.consent, purpose),
    }))
    .filter((row) => !(row.asked && !row.granted)),
);
</script>

<template>
  <section v-if="visible" class="ow-consent" :aria-label="text('widget.consent.heading')">
    <!-- The intro is shown only while something is still unanswered. Once every
         purpose has an answer, what remains on screen is the answers and the way
         to change them — repeating the pitch beside a granted row would be
         asking again. -->
    <p v-if="undecided.length > 0" class="ow-consent__intro">
      {{ text('widget.consent.intro') }}
    </p>

    <ul class="ow-consent__list">
      <li v-for="row in rows" :key="row.purpose" class="ow-consent__row">
        <span class="ow-consent__what">{{ text(PURPOSE_LABEL[row.purpose]) }}</span>
        <span v-if="row.granted" class="ow-consent__decided">
          <span class="ow-consent__state">{{ text('widget.consent.shared') }}</span>
          <!-- THE WITHDRAWAL. Same size, same place, one click — the UI half of
               Art. 7(3). `ghost` rather than `danger`: taking a fact back is not
               a destructive act the visitor should be warned about. -->
          <OwButton size="sm" variant="ghost" @click="emit('refuse', row.purpose)">
            {{ text('widget.consent.withdraw') }}
          </OwButton>
        </span>
        <span v-else class="ow-consent__choices">
          <!-- "No thanks" FIRST, and deliberately not styled as the lesser
               option. A refusal that is smaller, greyer or further away than the
               agreement is a nudge, and a nudged yes is not freely given. -->
          <OwButton size="sm" variant="ghost" @click="emit('refuse', row.purpose)">
            {{ text('widget.consent.decline') }}
          </OwButton>
          <OwButton size="sm" variant="secondary" @click="emit('grant', row.purpose)">
            {{ text('widget.consent.allow') }}
          </OwButton>
        </span>
      </li>
    </ul>
  </section>
</template>
